import { useEffect, useState, useCallback } from "react";
import { useBusiness } from "../../contexts/BusinessContext";
import { useNavigate } from "react-router-dom";
import { apiUrls } from "../../config/apiUrls.const";
import { Button, Spinner } from "react-bootstrap";
import { _post } from "../../services/apiClient";
import React from "react";
import { logout } from "../../services/authService";
import { getSecureItem } from "../../utils/LocalStorageManager";

interface Group {
  label: string;
  slug: string;
  status: boolean;
}

// Complete static configurations available in the system
const groupsConfig: Group[] = [
  { label: "SPA SERVICES", slug: "spa", status: false },
  { label: "PARLOR SERVICES", slug: "parlor", status: false },
  { label: "SALON SERVICES", slug: "salon", status: false }
];

export default function ServiceList() {
  const { business } = useBusiness();
  const biz_code = getSecureItem("binfo")?.[0]?.biz_code;
  const navigate = useNavigate();

  // Active item & Tab States
  const [itemsData, setItemsData] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>(""); // Starts empty, changes dynamically on load
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination States matching Laravel LengthAwarePaginator
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  // Categorization Mapping & Dropdown States
  const [categorization, setCategorization] = useState<any>({});
  const [categories, setCategories] = useState<any[]>([]);
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null);

  // Filter static setups against actual runtime object keys returned by API
  const groups = groupsConfig.filter((group) =>
    categorization && Object.keys(categorization).includes(group.slug)
  );

  // 1. Fetch Categorization mapping (Runs once to build dropdown dependencies)
  useEffect(() => {
    const fetchBizCategorization = async () => {
      if (!biz_code) return;
      try {
        const res: any = await _post(apiUrls.getBusinessCategorization, { biz_code });
        if (res?.data?.success) {
          const data = res?.data?.data || {};
          setCategorization(data);
          
          // Compute the first available tab dynamically instead of choosing 'spa' blindly
          const availableSlugs = Object.keys(data);
          if (availableSlugs.length > 0) {
            const defaultTab = availableSlugs[0];
            setActiveTab(defaultTab);
            setCategories(data[defaultTab] || []);
          } else {
            setLoading(false); // No categories returned at all
          }
        }
      } catch (err) {
        console.error("Failed to fetch categorized mappings", err);
        setLoading(false);
      }
    };

    fetchBizCategorization();
  }, [biz_code]);

  // 2. Paginated Item Fetcher Function (Fires exactly when page/tabs change)
  const fetchItems = useCallback(async (pageNumber = 1) => {
    if (!biz_code || !activeTab) return;

    setLoading(true);
    try {
      const res: any = await _post(apiUrls.items, {
        biz_code: biz_code,
        group: activeTab, 
        category_id: categoryId || undefined,
        subcategory_id: subcategoryId || undefined,
        page: pageNumber,
        per_page: perPage
      });

      const rootJson = res.data?.success !== undefined ? res.data : res;

      if (rootJson?.success && rootJson?.data) {
        const paginationData = rootJson.data;
        setError(null);
        
        setItemsData(paginationData?.data || []);
        setCurrentPage(paginationData?.current_page || 1);
        setLastPage(paginationData?.last_page || 1);
        setPerPage(paginationData?.per_page || 10);
        setTotalItems(paginationData?.total || 0);
      } else {
        setError(rootJson?.message || "Failed to load service items.");
      }
    } catch (err: any) {
      if (err?.response?.status === 401) {
        logout();
        return;
      }
      console.error(err);
      setError("Error fetching service entries.");
    } finally {
      setLoading(false);
    }
  }, [biz_code, activeTab, categoryId, subcategoryId, perPage]);

  // Hook trigger syncs queries dynamically when changing tabs
  useEffect(() => {
    if (activeTab) {
      fetchItems(1);
    }
  }, [activeTab, fetchItems]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      fetchItems(page);
    }
  };

  function updateTab(key: string) {
    setActiveTab(key);
    setCategories(categorization[key] || []);
    setCategoryId(null); 
    setSubcategories([]);
    setSubcategoryId(null);
    setItemsData([]); 
  }

  function updateSubcategory(e: React.ChangeEvent<HTMLSelectElement>) {
    const val = e.target.value ? Number(e.target.value) : null;
    setCategoryId(val);
    setSubcategoryId(null);
    if (!val) {
      setSubcategories([]);
      return;
    }
    const getCategory = categories.find((c: any) => c.category_id === val);
    setSubcategories(getCategory?.subcategories || []);
  }

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        Services
        <Button className="float-end" variant="primary" size="sm" onClick={() => navigate("/create-service")}>
          Create
        </Button>
      </h1>

      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-0 border-0">
              
              {/* Dynamic Tabs Container */}
              <ul className="nav nav-tabs">
                {groups.map((g) => (
                  <li className="nav-item" key={g.slug}>
                    <button
                      className={`nav-link ${activeTab === g.slug ? "active" : ""}`}
                      onClick={() => updateTab(g.slug)}
                    >
                      {g.label}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="tab-content border p-3">
                <div className="row mb-2">
                  <div className="col-12 mb-3">
                    <div className="row align-items-center">
                      <div className="col-md-3 text-md-end mb-2 mb-md-0">
                        <strong>Categorized Search</strong>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select"
                          value={categoryId ?? ""}
                          onChange={updateSubcategory}
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id || cat.category_id} value={cat.category_id}>
                              {cat.category?.name_en}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <select 
                          className="form-select"
                          value={subcategoryId ?? ""}
                          onChange={(e) => setSubcategoryId(e.target.value ? Number(e.target.value) : null)}
                        >
                          <option value="">Select Subcategory</option>
                          {subcategories.map((sub) => (
                            <option key={sub.id || sub.subcategory_id} value={sub.subcategory_id}>
                              {sub?.subcategory?.name_en}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <Button variant="primary" size="sm" className="px-3" onClick={() => fetchItems(1)}>
                          Search
                        </Button>
                      </div>
                    </div>
                  </div>
                  
                  <hr />

                  {error && <div className="alert alert-danger mx-2">{error}</div>}

                  {loading ? (
                    <div className="text-center py-5 w-100">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-2 text-muted small">Fetching items on-demand...</p>
                    </div>
                  ) : itemsData.length > 0 ? (
                    <>
                      <div className="table-responsive">
                        <table className="table table-hover table-striped align-middle">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Category</th>
                              <th>Subcategory</th>
                              <th>Title</th>
                              <th>Price</th>
                              <th>Duration</th>
                              <th>Default Image</th>
                              <th>Status</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {itemsData.map((bizItem: any) => (
                              <tr key={bizItem.id}>
                                <td>{bizItem?.id}</td>
                                <td>{bizItem?.category?.name_en}</td>
                                <td>{bizItem?.subcategory?.name_en}</td>
                                <td>{bizItem?.title}</td>
                                <td>{bizItem?.price} BDT</td>
                                <td>{bizItem?.duration}</td>
                                <td>
                                  {bizItem?.default_image ? (
                                    <img
                                      src={bizItem?.default_image?.image_url}
                                      alt=""
                                      className="img-fluid rounded"
                                      width="45"
                                      height="45"
                                    />
                                  ) : (
                                    <span className="text-muted small">No Image</span>
                                  )}
                                </td>
                                <td>
                                  <span className={`badge bg-${bizItem?.status ? 'success' : 'secondary'}`}>
                                    {bizItem?.status ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td>
                                  <Button className="float-end" variant="primary" size="sm" onClick={() => navigate(`/edit-service/${bizItem.id}`)}>
                                    Edit
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination UI Controls */}
                      {lastPage > 1 && (
                        <div className="d-flex justify-content-between align-items-center px-2 py-3 border-top mt-3">
                          <div className="text-muted small">
                            Showing entries {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalItems)} of {totalItems} entries
                          </div>
                          
                          <nav aria-label="Service navigation mapping">
                            <ul className="pagination d-flex list-unstyled align-items-center mb-0 gap-1">
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button 
                                  className="page-link rounded px-3 py-1" 
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                  style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
                                >
                                  &laquo;
                                </button>
                              </li>

                              {(() => {
                                let items = [];
                                for (let number = 1; number <= lastPage; number++) {
                                  if (number === 1 || number === lastPage || Math.abs(currentPage - number) <= 1) {
                                    items.push(
                                      <li key={number} className={`page-item ${number === currentPage ? 'active' : ''}`}>
                                        <button 
                                          className={`page-link rounded px-3 py-1 ${number === currentPage ? 'bg-primary text-white' : ''}`} 
                                          onClick={() => handlePageChange(number)}
                                        >
                                          {number}
                                        </button>
                                      </li>
                                    );
                                  } else if (number === 2 || number === lastPage - 1) {
                                    items.push(
                                      <li key={`ellipsis-${number}`} className="page-item disabled px-2">
                                        <span className="text-muted">&hellip;</span>
                                      </li>
                                    );
                                  }
                                }
                                return items;
                              })()}

                              <li className={`page-item ${currentPage === lastPage ? 'disabled' : ''}`}>
                                <button 
                                  className="page-link rounded px-3 py-1" 
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === lastPage}
                                  style={{ cursor: currentPage === lastPage ? 'not-allowed' : 'pointer' }}
                                >
                                  &raquo;
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center text-danger py-4 w-100">No Data Found for this section</div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}